import React, { useState } from 'react';

import { useHistory, useLocation } from 'react-router-dom';

import { CloseOutlined, DownOutlined, HomeFilled, HomeOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { Menu, notification } from 'antd';
import { theme } from 'theme';

import { Dropdown } from 'components/Dropdown';
import { Tooltip } from 'components/Tooltip';

import { globalAppStore, useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { captureAndShowError } from 'utils/error';
import { canSetAsHomePage, getStoredHomePage } from 'utils/homePage';

import * as Styled from './PrimaryNavigation.style';

/** Makes a non-button element activate with Enter and Space like a real button. */
function activateOnKey(e: React.KeyboardEvent, action: () => void) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault(); // stop Space from scrolling the page
    action();
  }
}

export const HomePageButton = () => {
  const history = useHistory();
  const location = useLocation();
  const { activeTenant, activeUser } = useGlobalApp();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const homePage = getStoredHomePage(activeTenant.tenantUser.settings?.homePage);
  const currentPage = `${location.pathname}${location.search}${location.hash}`;
  const isCurrentPageHome = homePage === currentPage;
  // the nav also renders on the not-found page, which can't be a home page
  const canSetCurrentPage = canSetAsHomePage(currentPage, activeTenant.slug);

  // an empty string clears the home page
  const saveHomePage = async (newHomePage: string, successMessage: string) => {
    try {
      const { data: updatedUser } = await coreApiFetch(schemas.users.updatePartialUser, {
        pathParams: { id: activeUser.id },
        bodyParams: { tenantUserSettings: { tenantId: activeTenant.id, settings: { homePage: newHomePage } } },
      });
      await globalAppStore.update({
        activeTenant: {
          ...activeTenant,
          tenantUser:
            updatedUser.tenants?.find((tenant) => tenant.id === activeTenant.id)?.tenantUser ?? activeTenant.tenantUser,
        },
        tenants: updatedUser.tenants,
      });
      notification.success({ message: successMessage });
    } catch (err) {
      captureAndShowError(err, 'Unable to update home page');
    }
  };

  // the dropdown is controlled (so the icon can open it), which means a menu click doesn't close it on its own
  const menu = (
    <Menu onClick={() => setIsMenuOpen(false)}>
      {/* on the home page itself the "This is your home page" row below replaces this one */}
      {homePage && !isCurrentPageHome && (
        <Menu.Item key="go" icon={<HomeOutlined />} onClick={() => history.push(homePage)}>
          Go to home page
        </Menu.Item>
      )}
      <Menu.Item
        key="set"
        icon={<HomeFilled />}
        disabled={isCurrentPageHome || !canSetCurrentPage}
        onClick={() => saveHomePage(currentPage, 'Home page set to this page')}
      >
        {isCurrentPageHome && 'This is your home page'}
        {!isCurrentPageHome && canSetCurrentPage && 'Set current page as home page'}
        {!isCurrentPageHome && !canSetCurrentPage && "This page can't be a home page"}
      </Menu.Item>
      {homePage && (
        <Menu.Item danger key="clear" icon={<CloseOutlined />} onClick={() => saveHomePage('', 'Home page cleared')}>
          Clear home page
        </Menu.Item>
      )}
    </Menu>
  );

  const goHomeOrOpenMenu = () => {
    if (homePage) {
      history.push(homePage);
    } else {
      setIsMenuOpen(true);
    }
  };

  return (
    <div
      className={css`
        display: flex;
        align-items: center;
        color: ${theme.colors.primary[100]};
        margin-right: 8px;
      `}
    >
      <Tooltip title={homePage ? 'Go to home page' : 'Set a home page'} placement="bottom">
        <Styled.IconOnlyButton
          className={css`
            && {
              margin-right: 0;
            }
          `}
          role="button"
          tabIndex={0}
          aria-label={homePage ? 'Go to home page' : 'Set a home page'}
          onClick={goHomeOrOpenMenu}
          onKeyDown={(e) => activateOnKey(e, goHomeOrOpenMenu)}
        >
          {homePage ? <HomeFilled /> : <HomeOutlined />}
        </Styled.IconOnlyButton>
      </Tooltip>
      <Dropdown overlay={menu} visible={isMenuOpen} onVisibleChange={setIsMenuOpen} placement="bottomRight">
        <span
          className={css`
            cursor: pointer;
            font-size: 0.6em;
            margin-left: 4px;
          `}
          role="button"
          tabIndex={0}
          aria-label="Home page options"
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          onKeyDown={(e) => activateOnKey(e, () => e.currentTarget.click())}
        >
          <DownOutlined />
        </span>
      </Dropdown>
    </div>
  );
};
